import { ProfileService } from '@/services/api/profile.service';
import { Title, Button, TextInput, Group, TagsInput, Grid, Menu, Text, Rating, Tooltip, ActionIcon } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import cx from 'clsx';
import { useForm } from '@mantine/form';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useEffect, useState } from 'react';
import { IconBrandLinkedin, IconChevronRight, IconCloudUpload, IconCopyPlus, IconEdit, IconFile, IconGripVertical, IconPlus, IconStar, IconTrash, IconUpload } from '@tabler/icons-react';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from 'react-i18next';
import { useListState } from '@mantine/hooks';
import { WorkExperience, Education } from '@/types/profile.types';
import { useProfileStore } from '@/store/useProfileStore';

import classes from './ProfilePage.module.css';
import { languages } from '@/contants/languages';
import React from 'react';
import i18n from '@/i18n';

const ProfilePage = () => {
  const {
    profiles,
    setProfiles,
    selectedProfile,
    setSelectedProfile,
    getProfileByLanguage,
    makeProfileDefault
  } = useProfileStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [value, setValue] = useState<string[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const { t } = useTranslation();
  const { axiosInstance, abortController } = useApi();
  const profileService = new ProfileService(axiosInstance);
  const [jobItemsState, jobItemsHandlers] = useListState<WorkExperience>([]);
  const [educationItemsState, educationItemsHandlers] = useListState<Education>([]);
  const form = useForm({
    initialValues: {
      firstname: '',
      lastname: '',
      email: ''
    }
  });

  useEffect(() => {
    if (selectedProfile) {
      form.setValues({
        firstname: selectedProfile.firstName || '',
        lastname: selectedProfile.lastName || '',
        email: selectedProfile.email || ''
      });
      setValue(selectedProfile.skills || []);
      jobItemsHandlers.setState(selectedProfile.workExperience || []);
      educationItemsHandlers.setState(selectedProfile.education || []);
    }
  }, [selectedProfile]);

  const fetchProfiles = async () => {
    // If profiles already exist in store, use them
    if (profiles.length > 0) {
      const defaultProfile = profiles.find((profile) => profile.isDefault);
      if (defaultProfile) {
        setSelectedProfile(defaultProfile);
        jobItemsHandlers.setState(defaultProfile.workExperience || []);
        educationItemsHandlers.setState(defaultProfile.education || []);
        setValue(defaultProfile.skills || []);
        form.setValues({
          firstname: defaultProfile.firstName || '',
          lastname: defaultProfile.lastName || '',
          email: defaultProfile.email || ''
        });
      }
      return;
    }

    try {
      setLoading(true);
      const response = await profileService.getProfiles();
      setProfiles(response.data);
      const defaultProfile = response.data.find((profile) => profile.isDefault);
      if (defaultProfile) {
        setSelectedProfile(defaultProfile);
        jobItemsHandlers.setState(defaultProfile.workExperience || []);
        educationItemsHandlers.setState(defaultProfile.education || []);
        setValue(defaultProfile.skills || []);
        form.setValues({
          firstname: defaultProfile.firstName || '',
          lastname: defaultProfile.lastName || '',
          email: defaultProfile.email || ''
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const ChangeProfile = async (language: string) => {
    const profile = getProfileByLanguage(language);
    if (profile) {
      setSelectedProfile(profile);
      jobItemsHandlers.setState(profile.workExperience || []);
      educationItemsHandlers.setState(profile.education || []);
      setValue(profile.skills || []);
      form.setValues({
        firstname: profile.firstName || '',
        lastname: profile.lastName || '',
        email: profile.email || ''
      });
    }
  }

  useEffect(() => {
    fetchProfiles();

    // Add refresh functionality to get the latest data when returning to the page
    const refreshProfileData = async () => {
      if (selectedProfile?._id) {
        try {
          const response = await profileService.getProfileById(selectedProfile._id);
          if (response.data) {
            // Update the selected profile with fresh data
            setSelectedProfile(response.data);
            
            // Update local state
            setValue(response.data.skills || []);
            jobItemsHandlers.setState(response.data.workExperience || []);
            educationItemsHandlers.setState(response.data.education || []);
            
            // Update form values
            form.setValues({
              firstname: response.data.firstName || '',
              lastname: response.data.lastName || '',
              email: response.data.email || ''
            });
            
            // Also update the profile in the profiles array
            const updatedProfiles = profiles.map(p => 
              p._id === response.data._id ? response.data : p
            );
            setProfiles(updatedProfiles);
          }
        } catch (err) {
          console.error('Failed to refresh profile data:', err);
        }
      }
    };
    
    // Call refresh function in addition to fetchProfiles
    refreshProfileData();

    return () => {
      abortController.abort();
    };
  }, []);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(languages.find(lang => lang.shortCode === i18n.language)?.isoCode, {
      year: 'numeric',
      month: 'short',
    });
  };

  const jobItems = jobItemsState
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((item, index) => (
    <Draggable key={item._id} index={index} draggableId={item._id}>
      {(provided, snapshot) => (
        <div
          className={cx(classes.item, { [classes.itemDragging]: snapshot.isDragging })}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div {...provided.dragHandleProps} className={classes.dragHandle}>
            <IconGripVertical size={18} stroke={1.5} />
          </div>
          <div style={{ flex: 1 }}>
            <Text>{item.jobTitle}</Text>
            <Text c="dimmed" size="sm">
              {item.company} • {formatDate(new Date(item.dateStart))} - {formatDate(new Date(item.dateEnd))}
            </Text>
          </div>
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={() => console.log('Edit:', item._id)}
              title="Edit"
            >
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Delete item',
                  children: 'Are you sure you want to delete this item?',
                  labels: {
                    confirm: 'Delete',
                    cancel: 'Cancel'
                  },
                  centered: true,
                  confirmProps: { color: 'red' },
                  onConfirm: () => {
                    const newItems = jobItemsState.filter((stateItem) => stateItem._id !== item._id);
                    jobItemsHandlers.setState(newItems);
                    if (selectedProfile) {
                      const newProfile = { ...selectedProfile, workExperience: newItems };
                      setSelectedProfile(newProfile);
                      makeProfileDefault(newProfile._id);
                    }
                  }
                })
              }
              }
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </div>
      )}
    </Draggable>
  ));

  const educationItems = educationItemsState
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((item, index) => (
    <Draggable key={item._id} index={index} draggableId={item._id}>
      {(provided, snapshot) => (
        <div
          className={cx(classes.item, { [classes.itemDragging]: snapshot.isDragging })}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div {...provided.dragHandleProps} className={classes.dragHandle}>
            <IconGripVertical size={18} stroke={1.5} />
          </div>
          <div style={{ flex: 1 }}>
            <Text>{item.degree} • {item.field}</Text>
            <Text c="dimmed" size="sm">
              {item.school} • {formatDate(new Date(item.dateStart))} - {formatDate(new Date(item.dateEnd))}
            </Text>
          </div>
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={() => console.log('Edit:', item._id)}
              title="Edit"
            >
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Delete item',
                  children: 'Are you sure you want to delete this item?',
                  labels: {
                    confirm: 'Delete',
                    cancel: 'Cancel'
                  },
                  centered: true,
                  confirmProps: { color: 'red' },
                  onConfirm: () => {
                    const newItems = educationItemsState.filter((stateItem) => stateItem._id !== item._id);
                    educationItemsHandlers.setState(newItems);
                    if (selectedProfile) {
                      const newProfile = { ...selectedProfile, education: newItems };
                      setSelectedProfile(newProfile);
                      makeProfileDefault(newProfile._id);
                    }
                  }
                })
              }}
              title="Delete"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </div>
      )}
    </Draggable>
  ));

  const handleMakeDefault = async (profileId: string) => {
    // e.stopPropagation();
    try {
      // Here you would typically make an API call to update the default profile
      // await profileService.makeProfileDefault(profileId);
      makeProfileDefault(profileId);
    } catch (error) {
      console.error('Failed to set default profile:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      if (!selectedProfile) return;
      
      const profileData = {
        firstName: form.values.firstname,
        lastName: form.values.lastname,
        email: form.values.email,
        skills: value,
        workExperience: jobItemsState,
        education: educationItemsState,
        language: selectedProfile.language,
        isDefault: selectedProfile.isDefault
      };
      
      setSaveLoading(true);
      const response = await profileService.updateProfile(selectedProfile._id, profileData);
      if (response.data) {
        // Ensure the skills array is properly set in the response data
        const updatedProfileData = {
          ...response.data,
          skills: response.data.skills || value // Fallback to current value if API doesn't return skills
        };
        
        // Update the profile in the store with the correct skills
        const updatedProfiles = profiles.map(p => 
          p._id === selectedProfile._id ? updatedProfileData : p
        );
        
        setProfiles(updatedProfiles);
        setSelectedProfile(updatedProfileData);
        
        // Explicitly update the skills state to ensure it's in sync
        setValue(updatedProfileData.skills || []);
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      setError('Failed to save profile');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div style={{ padding: '1rem' }}>
      <Group justify='space-between'>
        <Title order={2}>{t('edit_profile')}</Title>
        <Group justify='end' align='end'>
          <Tooltip label="Save">
            <Button 
              variant="filled" 
              onClick={handleSaveProfile}
              loading={saveLoading}
            >
              <IconCloudUpload size={24} />
            </Button>
          </Tooltip>

          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button>{t('profile')}: {languages.find((l) => l.shortCode === selectedProfile?.language)?.label} <IconChevronRight size={18} /></Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>{t('profiles')}</Menu.Label>
              {[...profiles].sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1)).map((profile) => (
                <Menu.Item
                  key={profile._id}
                  onClick={() => ChangeProfile(profile.language)}
                  rightSection={
                    <Rating
                      count={1}
                      value={profile.isDefault ? 1 : 0}
                      color='yellow'
                      size={18}
                      readOnly
                    />
                  }
                >
                  {languages.find((lng) => lng.shortCode === profile.language)?.label}
                </Menu.Item>
              ))}

              <Menu.Divider />

              <Menu.Label>New Profile</Menu.Label>
              <Menu.Item
                leftSection={<IconCopyPlus size={14} />}
              >
                Clone Profile
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFile size={14} />}
              >
                Start from Scratch
              </Menu.Item>

              <Menu.Item
                leftSection={<IconUpload size={14} />}
              >
                Upload Resume
              </Menu.Item>

              <Menu.Item
                leftSection={<IconBrandLinkedin size={14} />}
              >
                Import from LinkedIn
              </Menu.Item>

              <Menu.Divider />

              <Menu.Label>Current Profile</Menu.Label>
              <Menu.Item
                disabled={selectedProfile?.isDefault === true}
                leftSection={<IconStar size={14} />}
                onClick={() => handleMakeDefault(selectedProfile?._id || '')}
              >
                Set as Default
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
              >
                Delete Profile
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>


      </Group>
      <form onSubmit={form.onSubmit((values: typeof form.values) => console.log(values))}>
        <Grid gutter={{ base: 'md', lg: 'lg' }} mt={{ base: 'xs' }}>
          <Grid.Col span={{ base: 12 }} my={{ base: 'xs' }}>
            <Title order={4}>{t('personal_information')}</Title>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              withAsterisk
              label={t('firstname')}
              placeholder={t('firstname')}
              {...form.getInputProps('firstname')} />
          </Grid.Col>
          <Grid.Col span={{ base: 0, md: 6 }}>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              withAsterisk
              label={t('lastname')}
              placeholder={t('lastname')}
              {...form.getInputProps('lastname')} />
          </Grid.Col>
          <Grid.Col span={{ base: 0, md: 6 }}>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              withAsterisk
              label={t('email')}
              placeholder={t('email')}
              {...form.getInputProps('email')} />
          </Grid.Col>
        </Grid>

        <Grid gutter={{ base: 'md', lg: 'lg' }} mt={{ base: 'xs' }}>
          <Grid.Col span={{ base: 12 }} my={{ base: 'xs' }}>
            <Title order={4}>{t('skills')}</Title>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <TagsInput data={['React', 'Angular', 'Svelte']} value={value} onChange={setValue} />
          </Grid.Col>
        </Grid>

        <Grid gutter={{ base: 'md', lg: 'lg' }} mt={{ base: 'xs' }}>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <Group justify='space-between' my={{ base: 'xs' }}>
              <Title order={4}>{t('work_experience')}</Title>
              <Button
                type="button"
                onClick={() => {
                  const formRef = React.createRef<HTMLFormElement>();
                  modals.openConfirmModal({
                    title: t('add_work_experience'),
                    size: 'md',
                    onConfirm: () => {
                      if (!formRef.current) return;
                      const formData = new FormData(formRef.current);
                      const jobTitle = formData.get('jobTitle') as string;
                      const company = formData.get('company') as string;
                      const location = formData.get('location') as string;
                      const dateStart = formData.get('dateStart') as string;
                      const dateEnd = formData.get('dateEnd') as string;
                      const description = formData.get('description') as string;

                      jobItemsHandlers.append({
                        _id: crypto.randomUUID(),
                        jobTitle,
                        company,
                        location,
                        dateStart: new Date(dateStart),
                        dateEnd: new Date(dateEnd),
                        description,
                        order: Math.max(-1, ...jobItemsState.map(item => item.order || 0)) + 1
                      });
                    },
                    labels: { confirm: t('add'), cancel: t('cancel') },
                    children: (
                      <form ref={formRef}>
                        <Grid gutter="md">
                          <Grid.Col span={12}>
                            <TextInput
                              required
                              label={t('job_title')}
                              placeholder={t('job_title')}
                              name="jobTitle"
                            />
                          </Grid.Col>
                          <Grid.Col span={12}>
                            <TextInput
                              required
                              label={t('company')}
                              placeholder={t('company')}
                              name="company"
                            />
                          </Grid.Col>
                          <Grid.Col span={12}>
                            <TextInput
                              required
                              label={t('location')}
                              placeholder={t('location')}
                              name="location"
                            />
                          </Grid.Col>
                          <Grid.Col span={12}>
                            <MonthPickerInput
                              required
                              label={t('start_date')}
                              placeholder={t('start_date')}
                              name="dateStart"
                            />
                          </Grid.Col>
                          <Grid.Col span={12}>
                            <MonthPickerInput
                              label={t('end_date')}
                              placeholder={t('end_date')}
                              name="dateEnd"
                            />
                          </Grid.Col>
                          <Grid.Col span={12}>
                            <TextInput
                              required
                              label={t('description')}
                              placeholder={t('description')}
                              name="description"
                              maxLength={255}
                            />
                          </Grid.Col>
                        </Grid>
                      </form>
                    ),
                  })
                }}
              >
                <IconPlus size={16} />
              </Button>
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <DragDropContext
              onDragEnd={({ destination, source }) => {
                if (!destination) return;
                
                // Create a new array with the reordered items
                const items = Array.from(jobItemsState);
                const [removed] = items.splice(source.index, 1);
                items.splice(destination.index, 0, removed);
                
                // Update order values and set state
                const updatedItems = items.map((item, index) => ({
                  ...item,
                  order: index
                }));
                
                jobItemsHandlers.setState(updatedItems);
                
                // Update profile if it exists
                if (selectedProfile) {
                  const newProfile = { ...selectedProfile, workExperience: updatedItems };
                  setSelectedProfile(newProfile);
                  
                  // Immediately save the changes to the backend
                  (async () => {
                    try {
                      await profileService.updateProfile(newProfile._id, {
                        workExperiences: updatedItems
                      });
                      makeProfileDefault(newProfile._id);
                    } catch (error) {
                      console.error('Failed to update work experience order:', error);
                    }
                  })();
                }
              }}
            >
              <Droppable droppableId="work-experience-dnd-list" direction="vertical">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {jobItems}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid.Col>
        </Grid>

        <Grid gutter={{ base: 'md', lg: 'lg' }} mt={{ base: 'xs' }}>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <Group justify='space-between' my={{ base: 'xs' }}>
              <Title order={4}>{t('education')}</Title>
              <Button type="button" onClick={() => {
                const formRef = React.createRef<HTMLFormElement>();
                modals.openConfirmModal({
                  title: t('add_education'),
                  size: 'md',
                  onConfirm: () => {
                    if (!formRef.current) return;
                    const formData = new FormData(formRef.current);
                    const degree = formData.get('degree') as string;
                    const field = formData.get('field') as string;
                    const school = formData.get('school') as string;
                    const dateStart = formData.get('dateStart') as string;
                    const dateEnd = formData.get('dateEnd') as string;

                    educationItemsHandlers.append({
                      _id: crypto.randomUUID(),
                      degree,
                      field,
                      school,
                      dateStart: new Date(dateStart),
                      dateEnd: new Date(dateEnd),
                      order: Math.max(-1, ...educationItemsState.map(item => item.order || 0)) + 1
                    });
                  },
                  labels: { confirm: t('add'), cancel: t('cancel') },
                  children: (
                    <form ref={formRef}>
                      <Grid gutter="md">
                        <Grid.Col span={12}>
                          <TextInput
                            required
                            label={t('degree')}
                            placeholder={t('degree')}
                            name="degree"
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <TextInput
                            required
                            label={t('field')}
                            placeholder={t('field')}
                            name="field"
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <TextInput
                            required
                            label={t('school')}
                            placeholder={t('school')}
                            name="school"
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <MonthPickerInput
                            required
                            label={t('start_date')}
                            placeholder={t('start_date')}
                            name="dateStart"
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <MonthPickerInput
                            label={t('end_date')}
                            placeholder={t('end_date')}
                            name="dateEnd"
                          />
                        </Grid.Col>
                      </Grid>
                    </form>
                  ),
                })
              }}>
                <IconPlus size={16} />
              </Button>
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <DragDropContext
              onDragEnd={({ destination, source }) => {
                if (!destination) return;
                
                // Create a new array with the reordered items
                const items = Array.from(educationItemsState);
                const [removed] = items.splice(source.index, 1);
                items.splice(destination.index, 0, removed);
                
                // Update order values and set state
                const updatedItems = items.map((item, index) => ({
                  ...item,
                  order: index
                }));
                
                educationItemsHandlers.setState(updatedItems);
                
                // Update profile if it exists
                if (selectedProfile) {
                  const newProfile = { ...selectedProfile, education: updatedItems };
                  setSelectedProfile(newProfile);
                  
                  // Immediately save the changes to the backend
                  (async () => {
                    try {
                      await profileService.updateProfile(newProfile._id, {
                        educations: updatedItems
                      });
                      makeProfileDefault(newProfile._id);
                    } catch (error) {
                      console.error('Failed to update education order:', error);
                    }
                  })();
                }
              }}
            >
              <Droppable droppableId="education-dnd-list" direction="vertical">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {educationItems}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid.Col>
        </Grid>

      </form>

    </div>
  );
};

export default ProfilePage;